import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useQueryCacheResourceHelpers } from '../../resources/utils/useQueryCacheResourceHelpers.js';
import { useMutation } from '@tanstack/react-query';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useOptimisticallyUpdateInfiniteQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { isResourceListQueryKey } from '../../resources/utils/queryKeyGuards.js';

const useMutationChangeResourceLabel = () => {
  const { client } = useWoody();
  const { track } = useAnalytics();
  const { updateCachedResource } = useQueryCacheResourceHelpers();
  const accessToken = useContextAccessToken();
  const optimisticallyUpdateInfiniteQueries = useOptimisticallyUpdateInfiniteQueriesWithPredicate();
  const mutation = useMutation({
    mutationFn: async ({ resourceId, label }) => {
      return client.v2({
        endpoint: "/v2/resources/{resourceId}/color-labels",
        params: {
          resourceId
        }
      }, {
        method: "patch",
        body: {
          colorLabelId: label?.id || null
        },
        query: {
          accessToken
        }
      });
    },
    onMutate: ({ resourceId, label: assignedLabel }) => {
      const updateCachedResourceControls = updateCachedResource({
        id: resourceId,
        label: assignedLabel
      });
      let itemMoved = null;
      const queriesItemRemovedFrom = optimisticallyUpdateInfiniteQueries({
        predicate: (query) => {
          if (isResourceListQueryKey(query.queryKey) && query.queryKey[1]) {
            const label = query.queryKey[1].label;
            if (label !== (assignedLabel?.id || null) && label !== void 0) {
              return true;
            }
          }
          return false;
        },
        pageUpdater: (page) => {
          const nextResources = page.resources.filter((r) => {
            if (r.id === resourceId) {
              itemMoved = r;
              return false;
            }
            return true;
          });
          return {
            ...page,
            resources: nextResources,
            total: nextResources.length
          };
        }
      });
      const queriesItemAddedTo = itemMoved ? optimisticallyUpdateInfiniteQueries({
        predicate: (query) => {
          if (isResourceListQueryKey(query.queryKey) && query.queryKey[1]) {
            const label = query.queryKey[1].label;
            if (label === (assignedLabel?.id || null) && label !== void 0) {
              return true;
            }
          }
          return false;
        },
        pageUpdater: (page, index) => {
          if (index === 0) {
            return {
              ...page,
              total: page.total + 1,
              resources: [
                itemMoved,
                ...page.resources
              ]
            };
          }
          return page;
        }
      }) : null;
      return {
        resetCacheToPreOptimisticState: () => {
          updateCachedResourceControls.resetCacheToPreOptimisticState();
          queriesItemRemovedFrom.resetQueriesData();
          queriesItemAddedTo?.resetQueriesData();
        },
        invalidateQueries: () => {
          updateCachedResourceControls.invalidateQueries();
          queriesItemRemovedFrom.invalidateQueries();
          queriesItemAddedTo?.invalidateQueries();
        }
      };
    },
    onError: (_, _v, context) => {
      toast({
        content: "Failed to change label"
      });
      context?.resetCacheToPreOptimisticState();
      context?.invalidateQueries();
    },
    onSuccess: (_, { label, resourceId }) => {
      track(label ? AnalyticsEvents.ChangeLabel : AnalyticsEvents.RemovedLabel, {
        resourceId,
        labelId: label?.id
      });
    }
  });
  return mutation;
};

export { useMutationChangeResourceLabel };
//# sourceMappingURL=useMutationChangeResourceLabel.js.map
